/**
 * <scroll-rail> — the carousel used by every card section.
 *
 * The scrolling itself is native: CSS scroll-snap on an overflow container.
 * This element only adds what CSS cannot do — arrow buttons, dots, and the
 * "this fits, so drop the rail" check. No carousel library, ~2 KB.
 *
 * Markup contract:
 *   <scroll-rail>
 *     <ul class="rail" data-rail>…</ul>
 *     <div class="rail-controls" data-rail-controls>
 *       <button data-rail-prev>  <button data-rail-next>
 *     </div>
 *     <div class="rail-dots" data-rail-dots></div>   (optional)
 *   </scroll-rail>
 */
(function () {
  'use strict';

  if (customElements.get('scroll-rail')) return;

  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail]');
      if (!this.track) return;

      this.controls = this.querySelector('[data-rail-controls]');
      this.dotsHost = this.querySelector('[data-rail-dots]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.items = Array.from(this.track.children);

      this.prev?.addEventListener('click', () => this.scrollByPage(-1));
      this.next?.addEventListener('click', () => this.scrollByPage(1));

      this.onScroll = window.Theme.utils.throttle(() => this.update(), 120);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // Re-measure when the viewport changes; a rail that fit at 1440px may
      // overflow at 1024px.
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);

      this.buildDots();
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    get overflows() {
      return this.track.scrollWidth - this.track.clientWidth > 2;
    }

    scrollByPage(direction) {
      // One "page" is the visible width minus a sliver, so the item at the
      // edge stays partly visible and the customer keeps their place.
      const amount = Math.max(this.track.clientWidth * 0.85, 200);
      // Smooth scrolling is an animation, so it asks the shared helper
      // rather than gliding past someone who turned motion down.
      this.track.scrollBy({ left: amount * direction, behavior: window.Theme.utils.scrollBehavior() });
    }

    /**
     * A page can carry several rails, so a dot cannot just say "1 / 4" — a
     * screen reader would read the same name for every one of them. The rail
     * names itself from its section heading when it has one.
     */
    dotLabel(index) {
      const strings = window.Theme.strings || {};
      const name = (this.dataset.railName || '').trim();
      const template = name ? strings.goToSlideIn : strings.goToSlide;
      return (template || '[index] / [total]')
        .replace('[index]', String(index + 1))
        .replace('[total]', String(this.items.length))
        .replace('[name]', name);
    }

    buildDots() {
      if (!this.dotsHost || this.items.length < 2) return;

      this.dots = this.items.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rail-dots__dot';
        dot.setAttribute('aria-label', this.dotLabel(index));
        dot.addEventListener('click', () => {
          this.items[index].scrollIntoView({
            behavior: window.Theme.utils.scrollBehavior(),
            block: 'nearest',
            inline: 'start',
          });
        });
        this.dotsHost.appendChild(dot);
        return dot;
      });
    }

    update() {
      const overflows = this.overflows;

      this.track.classList.toggle('rail--fits', !overflows);
      if (this.controls) this.controls.dataset.hasOverflow = String(overflows);
      if (this.dotsHost) this.dotsHost.hidden = !overflows;

      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      // RTL reports a negative scrollLeft; compare on the absolute value.
      const position = Math.abs(scrollLeft);

      if (this.prev) this.prev.disabled = position < 4;
      if (this.next) this.next.disabled = position + clientWidth >= scrollWidth - 4;

      if (this.dots) {
        const active = this.items.findIndex((item) => {
          const offset = Math.abs(item.offsetLeft - this.track.offsetLeft);
          return offset >= position - 4 && offset <= position + clientWidth / 2;
        });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === Math.max(active, 0))));
      }
    }
  }

  customElements.define('scroll-rail', ScrollRail);
})();
